import React, { useCallback, useMemo, useState } from "react";
import {
  TextField,
  Divider,
  Box,
  Badge,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import AccountCircle from "@material-ui/icons/AccountCircle";
import LockIcon from "@material-ui/icons/Lock";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PersonIcon from "@material-ui/icons/Person";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import { styles } from "./styles";
import logoutIcon from "../../img/logout1.png";
import NotificationsIcon from "@material-ui/icons/Notifications";
import NotificationDrawer from "../NotificationManagment/AlertList";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "./ChangePassword";
import config from "../../config/config";
import ChatIcon from "@material-ui/icons/Chat";
import { LoginAction } from "../../actions/login";
import { useSnackbar } from "../CommonComponents/SnackbarProvider";

const useStyles = makeStyles((theme) => ({
  userButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "6px",
    transition: "all 0.2s ease",
    background: "transparent",
    border: "none",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
    },
  },

  menu: {
    marginTop: theme.spacing(0.5),
  },
  // menuPaper: {
  //   borderRadius: "8px",
  //   minWidth: "260px",
  //   boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  //   marginTop: theme.spacing(1),
  //   overflow: "visible",
  // },
  menuPaper: {
    borderRadius: "16px",
    minWidth: "280px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    marginTop: theme.spacing(1),
    overflow: "visible",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -5,
      right: 20,
      width: 16,
      height: 16,
      backgroundColor: "#fff",
      transform: "rotate(45deg)",
      boxShadow: "-3px -3px 5px rgba(0, 0, 0, 0.04)",
    },
  },
  menuItem: {
    padding: "12px 16px",
    transition: "all 0.2s ease",
    borderRadius: "0px",
    margin: "0px",
    fontSize: "0.9rem",
    "&:hover": {
      backgroundColor: "#f0f4f8",
      "& $menuItemIcon": {
        color: "#003f87",
      },
    },
  },
  menuItemIcon: {
    minWidth: "40px",
    color: "#666",
    transition: "all 0.2s ease",
    "& svg": {
      fontSize: 20,
    },
  },
  menuItemText: {
    "& .MuiTypography-root": {
      fontWeight: 500,
      fontSize: "0.9rem",
      color: "#2d3748",
    },
  },
  divider: {
    margin: "0px",
    backgroundColor: "rgba(0, 0, 0, 0.06)",
  },
  userName: {
    fontWeight: 500,
    color: "#fff",
    fontSize: "0.9rem",
    letterSpacing: "0.3px",
  },
  userEmail: {
    fontSize: "0.75rem",
    color: "rgba(255, 255, 255, 0.8)",
    marginTop: "2px",
  },
  avatar: {
    width: 40,
    height: 40,
    backgroundColor: "#003f87",
    fontWeight: 700,
    fontSize: "0.95rem",
    border: "2px solid #fff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  },
  logoutItem: {
    color: "#e53e3e",
    "&:hover": {
      backgroundColor: "rgba(229, 62, 62, 0.06)",
      "& $menuItemIcon": {
        color: "#e53e3e",
      },
    },
  },
  arrowIcon: {
    fontSize: 18,
    color: "#fff",
    transition: "transform 0.2s ease",
  },
  arrowIconOpen: {
    transform: "rotate(180deg)",
  },
  notification: {
    marginRight: "2px",
  },
  logoutDialog: {
    borderRadius: "12px",
    padding: "8px",
    background: "#ffffff",
    boxShadow: "0 16px 40px rgba(0, 0, 0, 0.1)",
    minWidth: "380px",
    maxWidth: "420px",
  },
  cancelButton: {
    borderRadius: "6px",
    padding: "8px 20px",
    fontWeight: 600,
    fontSize: "0.85rem",
    border: "1px solid #cbd5e0",
    color: "#4a5568",
    textTransform: "none",
    minWidth: "90px",
    "&:hover": {
      border: "1px solid #a0aec0",
      backgroundColor: "#f7fafc",
    },
  },
  logoutButton: {
    borderRadius: "6px",
    padding: "8px 20px",
    fontWeight: 600,
    fontSize: "0.85rem",
    background: "#003f87",
    color: "#fff",
    textTransform: "none",
    minWidth: "90px",
    "&:hover": {
      background: "#002d5f",
    },
  },
  btn: {
    background: "linear-gradient(180deg, #1262bdff, #002d5f)",
    color: "#ffffff",
    fontWeight: 600,
    borderRadius: "999px", // 🔹 pill shape
    textTransform: "none",
    marginRight: "12px",
    padding: "6px 18px",
    fontSize: "0.85rem",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",

    "&:hover": {
      background: "linear-gradient(180deg, #2b80e0, #155bb8)",
      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
    },
  },
}));

const RightSideHeader = React.memo(
  ({ events, setEvents, raiseAnIssueHandler, isOpen, setOpen }) => {
    const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);
    const handleLogoutConfirm = () => {
      handleMenuClose();
      setOpenLogoutConfirm(true);
    };

    const { showSnackbar } = useSnackbar();

    const handleLogout = useCallback(async () => {
      setOpenLogoutConfirm(false);

      try {
        const result = await LoginAction.logOutAndClearToken(showSnackbar);

        if (result) {
          sessionStorage.clear();
          window.open("/", "_self");
        } else {
          showSnackbar("Logout API failed. Please try again.", "error");
        }
      } catch (error) {
        showSnackbar("Logout API failed. Please try again.", "error");
      }
    }, []);

    const handleCancelLogout = () => {
      setOpenLogoutConfirm(false);
    };

    const classes = useStyles();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [openChangePassword, setOpenChangePassword] = useState(false);

    const handleMenuOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleChangePasswordClick = () => {
      handleMenuClose();
      setOpenChangePassword(true);
    };

    // ✅ Memoize user display name (avoids recomputing on re-render)
    const userName = useMemo(() => {
      const email = sessionStorage.getItem("email");
      if (email === "route@watsoo.com") return "Route";
      return JSON.parse(sessionStorage.getItem("data"))?.name || "User";
    }, []);
    const userImage = useMemo(() => {
      const image = JSON.parse(sessionStorage.getItem("data"));
      return image?.imgUrl || "";
    }, []);

    const userEmail = useMemo(() => {
      return sessionStorage.getItem("email") || "user@example.com";
    }, []);

    // Get user initials for avatar
    const userInitials = useMemo(() => {
      return userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }, [userName]);

    const open = Boolean(anchorEl);

    return (
      <div style={styles.rightSubCont} id="nav_header">
        <span>
          <Button
            className={classes.btn}
            startIcon={<ChatIcon />}
            onClick={() => raiseAnIssueHandler()}
          >
            Raise an Issue
          </Button>
        </span>
        <span className={classes.notification}>
          <NotificationDrawer events={events} setEvents={setEvents} />
        </span>

        {/* User Profile Button with Hover */}
        <span
          className={classes.userButton}
          onClick={handleMenuOpen}
          aria-controls="user-menu"
          aria-haspopup="true"
        >
          {userImage ? (
            <Avatar
              className={classes.avatar}
              alt="User Avatar"
              src={userImage}
            />
          ) : (
            <Avatar className={classes.avatar}>{userInitials}</Avatar>
          )}

          <b className={classes.userName}>{userName}</b>
          <KeyboardArrowDown
            className={`${classes.arrowIcon} ${
              open ? classes.arrowIconOpen : ""
            }`}
          />
        </span>

        {/* Dropdown Menu */}
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          className={classes.menu}
          classes={{ paper: classes.menuPaper }}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          TransitionComponent={Fade}
          transitionDuration={300}
        >
          {/* Change Password Option */}
          <MenuItem
            onClick={handleChangePasswordClick}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <LockIcon />
            </ListItemIcon>
            <ListItemText
              primary="Change Password"
              className={classes.menuItemText}
            />
          </MenuItem>

          <Divider className={classes.divider} />

          {/* Logout Option */}
          <MenuItem
            onClick={handleLogoutConfirm}
            className={`${classes.menuItem} ${classes.logoutItem}`}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" className={classes.menuItemText} />
          </MenuItem>
        </Menu>

        {/* Change Password Modal */}
        <ChangePasswordModal
          open={openChangePassword}
          setOpen={setOpenChangePassword}
        />

        {/* Logout Confirmation Dialog */}
        <Dialog
          open={openLogoutConfirm}
          onClose={handleCancelLogout}
          aria-labelledby="logout-dialog-title"
          PaperProps={{
            className: classes.logoutDialog,
          }}
        >
          <DialogContent
            style={{ textAlign: "center", padding: "32px 24px 24px" }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <Box
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "#003f87",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(0, 63, 135, 0.2)",
                }}
              >
                <ExitToAppIcon
                  style={{
                    fontSize: "40px",
                    color: "white",
                    transform: "rotate(180deg)",
                  }}
                />
              </Box>
            </Box>

            <Typography
              variant="h6"
              style={{
                fontWeight: 700,
                color: "#2d3748",
                marginBottom: "8px",
                fontSize: "1.25rem",
              }}
            >
              Confirm Logout
            </Typography>

            <Typography
              variant="body1"
              style={{
                color: "#718096",
                fontSize: "0.95rem",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to logout from your account?
            </Typography>
          </DialogContent>

          <DialogActions
            style={{
              justifyContent: "center",
              gap: "12px",
              padding: "0 24px 24px",
              marginTop: "8px",
            }}
          >
            <Button
              onClick={handleCancelLogout}
              variant="outlined"
              className={classes.cancelButton}
            >
              Cancel
            </Button>

            <Button
              onClick={handleLogout}
              variant="contained"
              className={classes.logoutButton}
            >
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  },
);

export default RightSideHeader;
